import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageTransition from '../components/UI/PageTransition';
import { FaTrophy, FaShoppingBag, FaFutbol, FaBolt, FaUsers, FaMedal, FaBell, FaStar } from 'react-icons/fa';
import Avatar from '../components/UI/Avatar';
import CoinDisplay from '../components/UI/CoinDisplay';
import LevelBadge from '../components/UI/LevelBadge';
import DailyStreak from '../components/UI/DailyStreak';
import DailyQuests from '../components/UI/DailyQuests';
import Onboarding, { shouldShowOnboarding } from '../components/UI/Onboarding';
import PremiumBanner from '../components/UI/PremiumBanner';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(() => shouldShowOnboarding());

  // Notifications
  const [unreadCount, setUnreadCount]   = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs]     = useState(false);
  const notifsRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const fetch = () => {
      api.get('/notifications?limit=10')
        .then(({ data }) => {
          setUnreadCount(data.unreadCount || 0);
          setNotifications(data.notifications || []);
        }).catch(() => {});
    };
    fetch();
    const id = setInterval(fetch, 30000);
    return () => clearInterval(id);
  }, [user]);

  useEffect(() => {
    const handler = (e) => {
      if (notifsRef.current && !notifsRef.current.contains(e.target)) setShowNotifs(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markAllRead = () => {
    api.put('/notifications/read-all').catch(() => {});
    setUnreadCount(0);
    setNotifications((p) => p.map((n) => ({ ...n, is_read: true })));
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden:   { opacity: 0, y: 25 },
    visible:  { opacity: 1, y: 0, transition: { duration: 0.35 } },
  };

  return (
    <PageTransition variant="fade">
    {showOnboarding && <Onboarding onDone={() => setShowOnboarding(false)} />}
    <div
      className="app-container stadium-bg flex flex-col min-h-screen"
      style={{ paddingBottom: '90px' }}
    >
      {/* ── Lumières de stade en haut ── */}
      <div
        className="absolute top-0 left-0 right-0 h-40 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 15% 0%, rgba(240,180,41,0.18) 0%, transparent 50%), ' +
            'radial-gradient(ellipse at 85% 0%, rgba(240,180,41,0.18) 0%, transparent 50%)',
        }}
      />

      {/* ── Header ── */}
      <div className="flex items-center justify-between px-5 pt-5 pb-1 relative z-10">
        <div className="flex items-center gap-3">
          <Avatar
            user={user}
            size="md"
            showBorder
            onClick={() => navigate(`/profile/${user?.id || 'me'}`)}
          />
          <div>
            <p className="text-white/50 text-xs">Bienvenue,</p>
            <p className="text-white font-bold text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              {user?.pseudo || 'Joueur'}
            </p>
            <LevelBadge level={user?.level || 1} size="xs" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <DailyStreak />
          <CoinDisplay coins={user?.coins || 0} />
          {/* Cloche notifications */}
          <div className="relative" ref={notifsRef}>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => { setShowNotifs((v) => !v); if (!showNotifs && unreadCount > 0) markAllRead(); }}
              className="w-9 h-9 rounded-full flex items-center justify-center relative"
              style={{ background: 'rgba(255,255,255,0.1)' }}
            >
              <FaBell size={14} className="text-white" />
              {unreadCount > 0 && (
                <span style={{
                  position: 'absolute', top: -2, right: -2, width: 16, height: 16,
                  borderRadius: '50%', background: '#ef4444', color: '#fff',
                  fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </motion.button>
            {showNotifs && (
              <div style={{
                position: 'absolute', right: 0, top: 44, width: 280,
                background: '#1a4a2e', border: '1px solid rgba(240,180,41,0.3)',
                borderRadius: 12, zIndex: 9999, boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
                maxHeight: 320, overflowY: 'auto',
              }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <span style={{ color: '#f0b429', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: '0.85rem' }}>
                    NOTIFICATIONS
                  </span>
                </div>
                {notifications.length === 0 ? (
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', textAlign: 'center', padding: '16px' }}>
                    Aucune notification
                  </p>
                ) : notifications.map((n) => (
                  <div key={n.id} style={{
                    padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)',
                    background: n.is_read ? 'transparent' : 'rgba(240,180,41,0.08)',
                  }}>
                    <p style={{ color: '#fff', fontSize: '0.8rem', margin: 0, fontWeight: n.is_read ? 400 : 600 }}>
                      {n.title || n.type}
                    </p>
                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', margin: '2px 0 0' }}>
                      {n.message}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Bannière Premium (utilisateurs free) ── */}
      <PremiumBanner variant="compact" className="px-5 pt-2" />

      {/* ── Logo + Slogan OM ── */}
      <motion.div
        className="flex flex-col items-center py-2 px-5 relative z-10"
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, type: 'spring', stiffness: 150 }}
      >
        {/* Logo OM */}
        <div className="relative mb-1" style={{ lineHeight: 0 }}>
          <div
            className="w-24 h-24 rounded-full flex items-center justify-center font-black text-3xl"
            style={{
              background: 'linear-gradient(135deg, #003DA5 0%, #001a4d 100%)',
              color: '#FFB81C',
              boxShadow: '0 0 30px rgba(0,61,165,0.6), 0 0 60px rgba(255,184,28,0.4)',
              border: '3px solid #FFB81C',
              fontFamily: "'Rajdhani', sans-serif",
            }}
          >
            OM
          </div>
        </div>

        <h1
          className="text-4xl font-black tracking-widest uppercase text-center leading-none"
          style={{
            fontFamily: "'Rajdhani', sans-serif",
            color: '#003DA5',
            textShadow: '0 0 30px rgba(0,61,165,0.4), 0 2px 0 rgba(0,0,0,0.5)',
          }}
        >
          OM Quiz
        </h1>
        <h2
          className="text-lg font-black tracking-[0.3em] uppercase text-white/80 mb-2"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
        >
          Droit au But
        </h2>
        <p className="text-white/50 text-sm text-center tracking-wide">
          Teste ta culture OM. Deviens expert!
        </p>
      </motion.div>

      {/* ── Quêtes quotidiennes ── */}
      <DailyQuests />

      {/* ── Boutons de jeu ── */}
      <motion.div
        className="flex flex-col gap-3 px-5 relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* JOUER - OM Blue */}
        <motion.button
          variants={itemVariants}
          onClick={() => navigate('/quiz')}
          whileTap={{ scale: 0.97 }}
          className="w-full py-5 rounded-2xl font-black text-2xl uppercase tracking-widest flex items-center justify-center gap-3 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #003DA5 0%, #001a4d 100%)',
            boxShadow: '0 6px 30px rgba(0,61,165,0.55), 0 0 0 1px rgba(255,184,28,0.3)',
            fontFamily: "'Rajdhani', sans-serif",
            color: '#FFB81C',
          }}
        >
          <div className="absolute inset-0 shimmer opacity-40 pointer-events-none" />
          <FaFutbol size={22} />
          JOUER
        </motion.button>

        {/* CHRONO - Mode rapide 20 questions */}
        <motion.button
          variants={itemVariants}
          onClick={() => navigate('/chrono')}
          whileTap={{ scale: 0.97 }}
          className="w-full py-4 rounded-2xl font-black text-xl uppercase tracking-widest flex items-center justify-center gap-3"
          style={{
            background: 'linear-gradient(135deg, #FFB81C 0%, #E6A000 100%)',
            boxShadow: '0 4px 20px rgba(255,184,28,0.4)',
            fontFamily: "'Rajdhani', sans-serif",
            color: '#001a4d',
          }}
        >
          <FaBolt size={18} />
          CHRONO
        </motion.button>

        {/* DUEL */}
        <motion.button
          variants={itemVariants}
          onClick={() => navigate('/duel')}
          whileTap={{ scale: 0.97 }}
          className="w-full py-4 rounded-2xl font-black text-xl uppercase tracking-widest flex items-center justify-center gap-3"
          style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #166534 100%)',
            boxShadow: '0 4px 15px rgba(22,163,74,0.35)',
            fontFamily: "'Rajdhani', sans-serif",
            color: 'white',
            border: '1px solid rgba(34,197,94,0.25)',
          }}
        >
          <FaUsers size={18} />
          DUEL
        </motion.button>

        {/* TOURNOI */}
        <motion.button
          variants={itemVariants}
          onClick={() => navigate('/tournament')}
          whileTap={{ scale: 0.97 }}
          className="w-full py-3.5 rounded-2xl font-bold text-lg uppercase tracking-wider flex items-center justify-center gap-2"
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(240,180,41,0.25)',
            fontFamily: "'Rajdhani', sans-serif",
          }}
        >
          <FaMedal size={17} className="text-qfl-gold" />
          <span className="text-white/75">TOURNOI</span>
        </motion.button>

        {/* CHAMPIONNAT */}
        <motion.button
          variants={itemVariants}
          onClick={() => navigate('/championships')}
          whileTap={{ scale: 0.97 }}
          className="w-full py-3.5 rounded-2xl font-bold text-lg uppercase tracking-wider flex items-center justify-center gap-2"
          style={{
            background: 'linear-gradient(135deg, rgba(34,197,94,0.12), rgba(16,185,129,0.08))',
            border: '1px solid rgba(34,197,94,0.25)',
            fontFamily: "'Rajdhani', sans-serif",
          }}
        >
          <FaTrophy size={17} className="text-green-400" />
          <span className="text-green-400/90">CHAMPIONNAT</span>
          <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full bg-green-400/20 text-green-400 font-black" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            💰
          </span>
        </motion.button>

        {/* THÈMES PREMIUM */}
        <motion.button
          variants={itemVariants}
          onClick={() => navigate('/themes')}
          whileTap={{ scale: 0.97 }}
          className="w-full py-3.5 rounded-2xl font-bold text-lg uppercase tracking-wider flex items-center justify-center gap-2 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(240,180,41,0.15), rgba(240,180,41,0.06))',
            border: '1px solid rgba(240,180,41,0.35)',
            fontFamily: "'Rajdhani', sans-serif",
          }}
        >
          <FaStar size={15} className="text-qfl-gold" />
          <span className="text-qfl-gold">THÈMES</span>
          <span
            className="ml-1 text-xs px-1.5 py-0.5 rounded-full font-black"
            style={{
              background: 'rgba(240,180,41,0.2)',
              border: '1px solid rgba(240,180,41,0.4)',
              color: '#f0b429',
              fontFamily: "'Rajdhani', sans-serif",
            }}
          >
            PREMIUM
          </span>
        </motion.button>
      </motion.div>

      {/* ── Bas : Classement + Boutique ── */}
      <motion.div
        className="flex gap-3 px-5 pt-4 relative z-10 mt-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55 }}
      >
        <button
          onClick={() => navigate('/leaderboard')}
          className="flex-1 flex flex-col items-center gap-1.5 py-4 rounded-2xl transition-colors"
          style={{
            background: 'rgba(26,74,46,0.5)',
            border: '1px solid rgba(255,255,255,0.07)',
          }}
        >
          <FaTrophy size={22} className="text-qfl-gold" />
          <span className="text-white/70 text-xs font-bold uppercase tracking-wider" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            Classement
          </span>
        </button>

        <button
          onClick={() => navigate('/shop')}
          className="flex-1 flex flex-col items-center gap-1.5 py-4 rounded-2xl transition-colors"
          style={{
            background: 'rgba(26,74,46,0.5)',
            border: '1px solid rgba(255,255,255,0.07)',
          }}
        >
          <FaShoppingBag size={22} className="text-qfl-gold" />
          <span className="text-white/70 text-xs font-bold uppercase tracking-wider" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            Boutique
          </span>
        </button>
      </motion.div>
    </div>
    </PageTransition>
  );
}

export default Home;
